// srt-common-presets.js

// Vehicle presets and rules with allowed suspension types for both front and back end

// Used to setup the form when a vehicle type is selected, specifies allowed suspension types
// and default values for each vehicle type and axle group
// Also used to populate suspension <select> options and provide default values for user-defined suspensions

const LEGACY_DRIVE_KEYS = ['steelD', 'airDL', 'airDH'];
const LEGACY_TRAILER_KEYS = ['steelT', 'airTL', 'airTH'];
const STEER_PRESET_KEYS = ['g_steer_a_h', 'g_steer_a_l', 'g_steer_m_h', 'g_steer_m_l'];
const DRIVE_PRESET_KEYS = ['g_drive_a_h', 'g_drive_a_l', 'g_drive_m_h', 'g_drive_m_l'];
const TRAILER_PRESET_KEYS = [
   // Air – overslung
   'g_trailer_a_over_h',
   'g_trailer_a_over_h_ss',
   'g_trailer_a_over_l',
   'g_trailer_a_over_l_ss',
   // Air – underslung
   'g_trailer_a_under_h',
   'g_trailer_a_under_h_ss',
   'g_trailer_a_under_l',
   'g_trailer_a_under_l_ss',
   // Mechanical – overslung / underslung
   'g_trailer_m_over_h',
   'g_trailer_m_over_l',
   'g_trailer_m_under_h',
   'g_trailer_m_under_l',
];

export const VEHICLE_TYPE_RULES = {
   truck: {
      icon: 'truck.svg',
      allowRowOfEight: false,
      front: {
         min: 1,
         max: 2,
         default: 1,
         defaultTare: 3500, // total tare kg of front axle group
         defaultGross: 6000, // total gross kg of front axle group
         allowedSuspension: ['steer', ...STEER_PRESET_KEYS, 'user'],
         defaultSuspension: 'steer',
      },
      rear: {
         min: 1,
         max: 4,
         default: 2,
         defaultTare: 4000,
         defaultGross: 10000,
         allowedSuspension: [...LEGACY_DRIVE_KEYS, ...DRIVE_PRESET_KEYS, 'user'],
         defaultSuspension: 'steelD',
      },
   },

   'dog-trailer': {
      icon: 'dog-trailer.svg',
      allowRowOfEight: false,
      front: {
         min: 1,
         max: 4,
         default: 2,
         defaultTare: 3000,
         defaultGross: 6000,
         allowedSuspension: [...LEGACY_TRAILER_KEYS, ...TRAILER_PRESET_KEYS, 'user'],
         defaultSuspension: 'steelT',
      },
      rear: {
         min: 1,
         max: 4,
         default: 2,
         defaultTare: 3000,
         defaultGross: 9000,
         allowedSuspension: [...LEGACY_TRAILER_KEYS, ...TRAILER_PRESET_KEYS, 'user'],
         defaultSuspension: 'steelT',
      },
   },

   'semi-trailer': {
      icon: 'semi-trailer.svg',
      allowRowOfEight: false,
      front: {
         min: 0,
         max: 0,
         default: 0,
         defaultTare: 0,
         defaultGross: 0,
         allowedSuspension: [],
         defaultSuspension: null,
      },
      rear: {
         min: 1,
         max: 4,
         default: 3,
         defaultTare: 5000,
         defaultGross: 18000,
         allowedSuspension: [...LEGACY_TRAILER_KEYS, ...TRAILER_PRESET_KEYS, 'user'],
         defaultSuspension: 'steelT',
      },
   },

   'pig-trailer': {
      icon: 'pig-trailer.svg',
      allowRowOfEight: false,
      front: {
         min: 0,
         max: 0,
         default: 0,
         defaultTare: 0,
         defaultGross: 0,
         allowedSuspension: [],
         defaultSuspension: null,
      },
      rear: {
         min: 1,
         max: 4,
         default: 2,
         defaultTare: 4000,
         defaultGross: 12000,
         allowedSuspension: [...LEGACY_TRAILER_KEYS, ...TRAILER_PRESET_KEYS, 'user'],
         defaultSuspension: 'steelT',
      },
   },

   'jinker-trailer': {
      icon: 'jinker-trailer.svg',
      allowRowOfEight: false,
      front: {
         min: 0,
         max: 1,
         default: 0,
         defaultTare: 4500,
         defaultGross: 15000,
         allowedSuspension: [...LEGACY_TRAILER_KEYS, ...TRAILER_PRESET_KEYS, 'user'],
         defaultSuspension: 'steelT',
      },
      rear: {
         min: 1,
         max: 4,
         default: 3,
         defaultTare: 4500,
         defaultGross: 15000,
         allowedSuspension: [...LEGACY_TRAILER_KEYS, ...TRAILER_PRESET_KEYS, 'user'],
         defaultSuspension: 'steelT',
      },
   },

   'low-loader': {
      icon: 'low-loader.svg',
      allowRowOfEight: true,
      front: {
         min: 0,
         max: 0,
         default: 0,
         defaultTare: 0,
         defaultGross: 0,
         allowedSuspension: [],
         defaultSuspension: null,
      },
      rear: {
         min: 3,
         max: 6,
         default: 4,
         defaultTare: 8000,
         defaultGross: 22000,
         allowedSuspension: [...LEGACY_TRAILER_KEYS, ...TRAILER_PRESET_KEYS, 'hydra', 'user'],
         defaultSuspension: 'hydra',
      },
   },
};

// returns ['truck', 'dog-trailer', 'semi-trailer', 'pig-trailer', 'jinker-trailer', 'low-loader'] etc
export function getValidVehicleTypes() {
   return Object.keys(VEHICLE_TYPE_RULES);
}

//
// Suspension presets - these are used to populate the suspension type selects
// and provide default values for user-defined suspension types
//

export const LEGACY_SUSPENSION_PRESETS = {
   steer: {
      label: 'Steer Axle',
      editable: false,
      values: {
         rollStiffness: 130000,
         springStiffness: 185000,
         springSpacing: 0.8,
         lash: 0.015,
         rollCenterHeight: -0.02,
      },
   },
   steelD: {
      label: 'Steel Drive Axle',
      editable: false,
      values: {
         rollStiffness: 520000,
         springStiffness: 1000000,
         springSpacing: 0.97,
         lash: 0.03,
         rollCenterHeight: 0.2,
      },
   },
   airDL: {
      label: 'Air Drive Low',
      editable: false,
      values: { rollStiffness: 280000, springStiffness: 350000, springSpacing: 0.97, lash: 0.3, rollCenterHeight: 0.2 },
   },
   airDH: {
      label: 'Air Drive High',
      editable: false,
      values: { rollStiffness: 780000, springStiffness: 350000, springSpacing: 0.97, lash: 0.3, rollCenterHeight: 0.2 },
   },
   steelT: {
      label: 'Steel Trailer Axle',
      editable: false,
      values: {
         rollStiffness: 520000,
         springStiffness: 1000000,
         springSpacing: 0.97,
         lash: 0.03,
         rollCenterHeight: 0.2,
      },
   },
   airTL: {
      label: 'Air Trailer Low',
      editable: false,
      values: { rollStiffness: 280000, springStiffness: 350000, springSpacing: 0.97, lash: 0.3, rollCenterHeight: 0.2 },
   },
   airTH: {
      label: 'Air Trailer High',
      editable: false,
      values: { rollStiffness: 780000, springStiffness: 350000, springSpacing: 0.97, lash: 0.3, rollCenterHeight: 0.2 },
   },
   hydra: {
      label: 'Hydraulic Low-Loader',
      editable: false,
      values: {
         rollStiffness: 2500000,
         springStiffness: 2000000,
         springSpacing: 1.246,
         lash: 0.01,
         rollCenterHeight: 0.2,
      },
   },
};

/* NEW_SUSPENSION_PRESETS (rounded totals)
 * - Added `auxStiffness` (Nm/rad) = auxiliary/ARB roll stiffness.
 * - `rollStiffness` is TOTAL = auxStiffness + springStiffness * (springSpacing ** 2).
 * - Per your request, TOTAL `rollStiffness` values are rounded to the nearest 1000.
 * - `rollStiffnessDeg` remains the auxiliary value in Nm/deg for display/traceability.
 */
const NEW_SUSPENSION_PRESETS = {
   g_steer_a_h: {
      label: 'Generic steer air suspension (high stiffness)',
      editable: false,
      values: {
         rollStiffness: 297000,
         springStiffness: 150000,
         springSpacing: 0.95,
         auxStiffness: 229000,
         lash: 0.01,
         rollCenterHeight: -0.05,
         trackWidth: 2.03,
      },
   },

   g_steer_a_l: {
      label: 'Generic steer air suspension (low stiffness)',
      editable: false,
      values: {
         rollStiffness: 102000,
         springStiffness: 100000,
         springSpacing: 0.95,
         auxStiffness: 57000,
         lash: 0.01,
         rollCenterHeight: -0.05,
         trackWidth: 2.03,
      },
   },

   g_steer_m_h: {
      label: 'Generic steer mechanical suspension (high stiffness)',
      editable: false,
      values: {
         rollStiffness: 229000,
         springStiffness: 350000,
         springSpacing: 0.85,
         auxStiffness: 103000,
         lash: 0.02,
         rollCenterHeight: -0.02,
         trackWidth: 2.03,
      },
   },

   g_steer_m_l: {
      label: 'Generic steer mechanical suspension (low stiffness)',
      editable: false,
      values: {
         rollStiffness: 107000,
         springStiffness: 250000,
         springSpacing: 0.85,
         auxStiffness: 17000,
         lash: 0.02,
         rollCenterHeight: -0.02,
         trackWidth: 2.03,
      },
   },

   g_drive_a_h: {
      label: 'Generic drive air suspension (high stiffness)',
      editable: false,
      values: {
         rollStiffness: 1236000,
         springStiffness: 250000,
         springSpacing: 0.85,
         auxStiffness: 1146000,
         lash: 0.3,
         rollCenterHeight: 0.3,
         trackWidth: 1.85,
      },
   },

   g_drive_a_l: {
      label: 'Generic drive air suspension (low stiffness)',
      editable: false,
      values: {
         rollStiffness: 434000,
         springStiffness: 250000,
         springSpacing: 0.85,
         auxStiffness: 344000,
         lash: 0.3,
         rollCenterHeight: 0.3,
         trackWidth: 1.85,
      },
   },

   g_drive_m_h: {
      label: 'Generic drive mechanical suspension (high stiffness)',
      editable: false,
      values: {
         rollStiffness: 1160000,
         springStiffness: 2000000,
         springSpacing: 0.95,
         auxStiffness: 258000,
         lash: 0.03,
         rollCenterHeight: 0.1,
         trackWidth: 1.85,
      },
   },

   g_drive_m_l: {
      label: 'Generic drive mechanical suspension (low stiffness)',
      editable: false,
      values: {
         rollStiffness: 451000,
         springStiffness: 1000000,
         springSpacing: 0.95,
         auxStiffness: 0,
         lash: 0.03,
         rollCenterHeight: 0.1,
         trackWidth: 1.85,
      },
   },

   g_trailer_a_over_h: {
      label: 'Generic trailer air suspension (overslung, high stiffness)',
      editable: false,
      values: {
         rollStiffness: 1234000,
         springStiffness: 200000,
         springSpacing: 0.94,
         auxStiffness: 1146000,
         lash: 0.3,
         rollCenterHeight: 0.1,
         trackWidth: 1.85,
      },
   },

   g_trailer_a_over_h_ss: {
      label: 'Generic trailer air suspension (overslung, high stiffness, self-steered)',
      editable: false,
      values: {
         rollStiffness: 622000,
         springStiffness: 200000,
         springSpacing: 0.7,
         auxStiffness: 573000,
         lash: 0.3,
         rollCenterHeight: 0.1,
         trackWidth: 1.85,
      },
   },

   g_trailer_a_over_l: {
      label: 'Generic trailer air suspension (overslung, low stiffness)',
      editable: false,
      values: {
         rollStiffness: 661000,
         springStiffness: 200000,
         springSpacing: 0.94,
         auxStiffness: 573000,
         lash: 0.3,
         rollCenterHeight: 0.1,
         trackWidth: 1.85,
      },
   },

   g_trailer_a_over_l_ss: {
      label: 'Generic trailer air suspension (overslung, low stiffness, self-steered)',
      editable: false,
      values: {
         rollStiffness: 622000,
         springStiffness: 200000,
         springSpacing: 0.7,
         auxStiffness: 573000,
         lash: 0.3,
         rollCenterHeight: 0.1,
         trackWidth: 1.85,
      },
   },

   g_trailer_a_under_h: {
      label: 'Generic trailer air suspension (underslung, high stiffness)',
      editable: false,
      values: {
         rollStiffness: 1234000,
         springStiffness: 200000,
         springSpacing: 0.94,
         auxStiffness: 1146000,
         lash: 0.3,
         rollCenterHeight: -0.035,
         trackWidth: 1.85,
      },
   },

   g_trailer_a_under_h_ss: {
      label: 'Generic trailer air suspension (underslung, high stiffness, self-steered)',
      editable: false,
      values: {
         rollStiffness: 1195000,
         springStiffness: 200000,
         springSpacing: 0.7,
         auxStiffness: 1146000,
         lash: 0.3,
         rollCenterHeight: -0.035,
         trackWidth: 1.85,
      },
   },

   g_trailer_a_under_l: {
      label: 'Generic trailer air suspension (underslung, low stiffness)',
      editable: false,
      values: {
         rollStiffness: 661000,
         springStiffness: 200000,
         springSpacing: 0.94,
         auxStiffness: 573000,
         lash: 0.3,
         rollCenterHeight: -0.035,
         trackWidth: 1.85,
      },
   },

   g_trailer_a_under_l_ss: {
      label: 'Generic trailer air suspension (underslung, low stiffness, self-steered)',
      editable: false,
      values: {
         rollStiffness: 622000,
         springStiffness: 200000,
         springSpacing: 0.7,
         auxStiffness: 573000,
         lash: 0.3,
         rollCenterHeight: -0.035,
         trackWidth: 1.85,
      },
   },

   g_trailer_m_over_h: {
      label: 'Generic trailer mechanical suspension (overslung, high stiffness)',
      editable: false,
      values: {
         rollStiffness: 1000000,
         springStiffness: 2000000,
         springSpacing: 1.0,
         auxStiffness: 0,
         lash: 0.03,
         rollCenterHeight: 0.2,
         trackWidth: 1.85,
      },
   },

   g_trailer_m_over_l: {
      label: 'Generic trailer mechanical suspension (overslung, low stiffness)',
      editable: false,
      values: {
         rollStiffness: 450000,
         springStiffness: 900000,
         springSpacing: 1.0,
         auxStiffness: 0,
         lash: 0.03,
         rollCenterHeight: 0.2,
         trackWidth: 1.85,
      },
   },

   g_trailer_m_under_h: {
      label: 'Generic trailer mechanical suspension (underslung, high stiffness)',
      editable: false,
      values: {
         rollStiffness: 1000000,
         springStiffness: 2000000,
         springSpacing: 1.0,
         auxStiffness: 0,
         lash: 0.03,
         rollCenterHeight: -0.04,
         trackWidth: 1.85,
      },
   },

   g_trailer_m_under_l: {
      label: 'Generic trailer mechanical suspension (underslung, low stiffness)',
      editable: false,
      values: {
         rollStiffness: 450000,
         springStiffness: 900000,
         springSpacing: 1.0,
         auxStiffness: 0,
         lash: 0.03,
         rollCenterHeight: -0.04,
         trackWidth: 1.85,
      },
   },
};

export const SUSPENSION_PRESETS = {
   ...LEGACY_SUSPENSION_PRESETS,
   ...NEW_SUSPENSION_PRESETS,
   user: {
      label: 'User Defined',
      editable: true,
      values: { rollStiffness: null, springStiffness: null, springSpacing: null, lash: null, rollCenterHeight: null },
   },
};
